import { useSectionAccess } from "../../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { PanelLoading } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  catalogApi,
  type CategoryNode,
  type ProductStatus,
  type ProductSummary,
} from "../../../api/catalog";
import type { ApiError } from "../../../api/client";
import { Pagination } from "../../../components/Pagination";
import { Select } from "../../../components/Select";
import { useConfirm } from "../../../components/ConfirmDialog";
import { useToast } from "../../../components/Toast";
import { CatalogAdminTabs } from "../../../components/catalog/CatalogAdminTabs";

const PAGE_SIZE = 25;

export default function AdminProductsPage() {
  const { canWrite, canFull } = useSectionAccess("catalog");
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [items, setItems] = useState<ProductSummary[] | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [filterStatus, setFilterStatus] = useState<ProductStatus | "">("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The tree is already loaded for the filter; reuse it so the Category column
  // shows the translated name instead of the raw slug.
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (ns: CategoryNode[]) => ns.forEach((n) => { map.set(n.id, n.name); walk(n.children ?? []); });
    walk(categories);
    return map;
  }, [categories]);

  async function load() {
    setError(null);
    try {
      if (search.trim()) {
        // FTS endpoint isn't paginated — show first 50 hits and hide controls.
        const list = await catalogApi.searchProducts(search.trim(), i18n.resolvedLanguage ?? "en", 50);
        setItems(list);
        setTotalPages(0);
      } else {
        const result = await catalogApi.listProducts({
          status: filterStatus || undefined,
          categoryId: filterCategory || undefined,
          locale: i18n.resolvedLanguage ?? "en",
          page,
          size: PAGE_SIZE,
        });
        setItems(result.content.filter(Boolean));
        setTotalPages(result.totalPages);
      }
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  useEffect(() => {
    void catalogApi.tree(i18n.resolvedLanguage ?? "en").then(setCategories).catch(() => undefined);
  }, [i18n.resolvedLanguage]);

  // Reset to first page whenever filters change.
  useEffect(() => {
    setPage(0);
  }, [filterStatus, filterCategory, search]);

  useEffect(() => {
    void load();
  }, [filterStatus, filterCategory, page]);

  async function publish(id: string) {
    try {
      await catalogApi.publishProduct(id);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }
  async function unpublish(id: string) {
    await catalogApi.unpublishProduct(id);
    await load();
  }
  async function remove(p: ProductSummary) {
    if (!(await confirm({ message: t("admin.products.confirmDelete", { sku: p.sku }), danger: true }))) return;
    try {
      await catalogApi.deleteProduct(p.id);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.products.title")}</h1>
        {canWrite && <Link to="/admin/catalog/products/new" style={primary}>{t("admin.products.newProduct")}</Link>}
      </header>

      <CatalogAdminTabs active="products" />

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          placeholder={t("admin.products.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          style={{ ...input, minWidth: 260 }}
        />
        <Select<string>
          value={filterCategory}
          onChange={setFilterCategory}
          ariaLabel={t("admin.products.allCategories")}
          style={{ minWidth: 200 }}
          options={[
            { value: "", label: t("admin.products.allCategories") },
            ...flatten(categories).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select<ProductStatus | "">
          value={filterStatus}
          onChange={setFilterStatus}
          ariaLabel={t("admin.products.allStatuses")}
          style={{ minWidth: 190 }}
          options={[
            { value: "", label: t("admin.products.allStatuses") },
            { value: "DRAFT", label: t("admin.products.status.DRAFT") },
            { value: "PUBLISHED", label: t("admin.products.status.PUBLISHED") },
            { value: "ARCHIVED", label: t("admin.products.status.ARCHIVED") },
          ]}
        />
      </div>

      {error && <div style={errorBox}>{error}</div>}

      {!items ? (
        <PanelLoading text={t("common.loading")} />
      ) : items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.products.empty")}</p>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t("admin.products.col.sku")}</th>
              <th style={th}>{t("admin.products.col.name")}</th>
              <th style={th}>{t("admin.products.col.category")}</th>
              <th style={th}>{t("admin.products.col.status")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td style={td}><code>{p.sku}</code></td>
                <td style={td}>{p.name}</td>
                <td style={td}>{categoryNames.get(p.categoryId ?? "") ?? p.categorySlug ?? "—"}</td>
                <td style={td}><StatusBadge status={p.status} /></td>
                <td style={td}>
                  {canWrite && <Link to={`/admin/catalog/products/${p.id}`} style={linkBtn}>{t("admin.products.action.edit")}</Link>}
                  {/* An article from the ERP is published by the ERP import, which would undo either
                      button, so the server refuses both and the list does not offer them. */}
                  {p.fromErp ? null : p.status === "PUBLISHED"
                    ? <button type="button" disabled={!canWrite} onClick={() => unpublish(p.id)} style={linkBtn}>{t("admin.products.action.unpublish")}</button>
                    : <button type="button" disabled={!canWrite} onClick={() => publish(p.id)} style={linkBtn}>{t("admin.products.action.publish")}</button>}
                  <button type="button" disabled={!canFull} onClick={() => remove(p)} style={dangerLink}>{t("admin.products.action.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function flatten(nodes: CategoryNode[], depth = 0): { id: string; name: string }[] {
  return nodes.flatMap((n) => [
    { id: n.id, name: `${"— ".repeat(depth)}${n.name}` },
    ...flatten(n.children, depth + 1),
  ]);
}

function StatusBadge({ status }: { status: ProductStatus }) {
  const { t } = useTranslation();
  const palette: Record<ProductStatus, { bg: string; fg: string }> = {
    DRAFT: { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    PUBLISHED: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    ARCHIVED: { bg: "var(--warn-bg)", fg: "var(--warn)" },
  };
  const c = palette[status];
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {t(`admin.products.status.${status}`)}
    </span>
  );
}

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  background: "var(--surface)",
  borderRadius: 8,
  overflow: "hidden",
  boxShadow: "var(--shadow-1)",
};
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", textDecoration: "none", borderRadius: 6, fontSize: 14 };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", marginRight: 8, fontSize: 13, textDecoration: "none" };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
